import { useState, useTransition } from "react";
import { apiCall, useApi } from "../../api/client";
import { mutate } from "swr";
import { apiKey } from "../../api/types";
import { help } from "../../api/contracts/help";
import { Button } from "../../button";
import { Input } from "../../input";
import { SettingsSection } from "../settings/settings-section";
import type { HelpExternalLinkRow } from "../../types/help";

/**
 * /help/settings CRUD for external/community links. Uses the
 * help.links.list + help.links.set contracts; mutations invalidate
 * the cache so the user-dropdown and footer reflect changes
 * immediately.
 *
 * The schema reserves seven well-known kinds (slack, github,
 * support_email, status_page, changelog, x_twitter, youtube); the
 * editor lets you add additional kinds freely. Disabling a row
 * hides it from the dropdown without losing the URL.
 */
export function HelpExternalLinksEditor() {
  const { data, isLoading } = useApi(help.links.list, {
    query: { include_archived: false },
  });
  const links = data?.links ?? [];

  return (
    <SettingsSection
      title="External links"
      description="Slack, GitHub, support email, status page, changelog, social. Shown in the user dropdown and at the bottom of every help page."
    >
      {isLoading ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-3">
          {links.map((l) => (
            <LinkRow key={l.id} link={l} />
          ))}
          <NewLinkRow existingKinds={new Set(links.map((l) => l.kind))} />
        </div>
      )}
    </SettingsSection>
  );
}

function LinkRow({ link }: { link: HelpExternalLinkRow }) {
  const [url, setUrl] = useState(link.url);
  const [label, setLabel] = useState(link.label);
  const [enabled, setEnabled] = useState(link.enabled === 1);
  const [pending, startTransition] = useTransition();
  const [dirty, setDirty] = useState(false);

  async function save() {
    await apiCall(help.links.set, {
      body: {
        kind: link.kind,
        url,
        label,
        enabled,
      },
    });
    startTransition(() => {
      mutate(
        apiKey(help.links.list, { query: { include_archived: false } }),
      );
      mutate(
        apiKey(help.links.list, { query: { enabled_only: true } }),
      );
      setDirty(false);
    });
  }

  async function archive() {
    await apiCall(help.links.archive, { params: { id: link.id } });
    startTransition(() => {
      mutate(
        apiKey(help.links.list, { query: { include_archived: false } }),
      );
      mutate(
        apiKey(help.links.list, { query: { enabled_only: true } }),
      );
    });
  }

  return (
    <div className="grid grid-cols-1 gap-2 rounded-md border border-border/60 p-3 sm:grid-cols-[8rem_1fr_auto]">
      <div className="space-y-1">
        <span className="type-body text-muted-foreground">{link.kind}</span>
      </div>
      <div className="space-y-1.5">
        <Input
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setDirty(true);
          }}
          placeholder="https://…"
        />
        <Input
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
            setDirty(true);
          }}
          placeholder="Display label"
        />
        <label className="flex items-center gap-2 type-body text-muted-foreground">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
              setDirty(true);
            }}
          />
          Enabled
        </label>
      </div>
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={save}
          disabled={pending || !dirty}
        >
          {pending ? "Saving…" : "Save"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={archive}
          disabled={pending}
        >
          Remove
        </Button>
      </div>
    </div>
  );
}

function NewLinkRow({ existingKinds }: { existingKinds: Set<string> }) {
  const [kind, setKind] = useState("");
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  async function add() {
    setError(null);
    const trimmed = kind.trim().toLowerCase();
    if (!trimmed) {
      setError("Pick a kind (e.g. discord, bluesky, forum).");
      return;
    }
    if (existingKinds.has(trimmed)) {
      setError(`A link of kind '${trimmed}' already exists.`);
      return;
    }
    try {
      await apiCall(help.links.set, {
        body: { kind: trimmed, url, label, enabled: true },
      });
      startTransition(() => {
        mutate(
          apiKey(help.links.list, { query: { include_archived: false } }),
        );
        mutate(
          apiKey(help.links.list, { query: { enabled_only: true } }),
        );
        setKind("");
        setUrl("");
        setLabel("");
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Add failed");
    }
  }

  return (
    <div className="grid grid-cols-1 gap-2 rounded-md border border-dashed border-border/60 p-3 sm:grid-cols-[8rem_1fr_auto]">
      <Input
        value={kind}
        onChange={(e) => setKind(e.target.value)}
        placeholder="kind (e.g. discord)"
      />
      <div className="space-y-1.5">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…"
        />
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Display label"
        />
      </div>
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          size="sm"
          onClick={add}
          disabled={pending || !kind || !label}
        >
          Add
        </Button>
      </div>
      {error ? (
        <p className="sm:col-span-3 type-body text-destructive">{error}</p>
      ) : null}
    </div>
  );
}
